/******************************************************************************
 *
 * Copyright (c) 2019-2022 ServiceActivator Inc. All Rights Reserved.
 *
 * Unauthorized copying of the code in this file, completely or partially,
 * via any medium is strictly prohibited.
 *
 * Proprietary and Confidential.
 *
 * Written by 'Shivam Mishra' <shivam.mishra@wakencode.com>, June 2022
 *
 *****************************************************************************/

import React, { useEffect, useState } from "react";
import Styles from "./WorkDetails.module.css";
import { prepareEmployeeCount, prepareWorkDetails } from "../../container/postDataFormat";
import { updateInfo } from "../../network";
import { string } from "../../constant/string";
import Loader from "../../loader/WhiteLoader";
import { employeeCount } from "../../constant/constant";
import { useUser } from "../../context/UserContext";
import { useAuth } from "../../context/AuthContext";

/**
 * Work details page called in Wrapper.js
 * This page takes employee count and update in
 * local storage and api
 *
 * @param {String} setPage State for current page
 * @param {String} org State for user type
 * @param {String} setOrg Method to update state for user type
 * @returns JSX for the work detail/employee count page
 */
const WorkDetails = ({ setPage, org, setOrg }) => {
  // State for error
  const [error, setError] = useState(null);
  // State for loader
  const [loading, setLoading] = useState(false);
  // State for user type
  const [companySize, setCompanySize] = useState({
    userType: "",
    employeeCount: "",
  });
  //State to set user data from user context
  const { userData, setUserData } = useUser("");
  // State to set token from auth context
  const { token } = useAuth("");

  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  /**
   * Prepopulate data on screen if present in local storage
   */
  useEffect(() => {
    setCompanySize((prev) => ({
      ...prev,
      userType: userData?.subscriberDetails?.type,
      employeeCount: userData?.employeeCount,
    }));
  }, []);

  /**
   * Next button handler
   *
   * @param {*} e Event for Next button click
   * @returns {void}
   */
  const handleNextButtonChange = (e) => {
    e.preventDefault();
    if (!org) {
      setError(string.REQUIRED);
      return;
    }
    setLoading(true);
    setUserData((prev) => ({
      ...prev,
      subscriberDetails: {
        ...prev?.subscriberDetails,
        type: companySize.userType,
      },
      employeeCount: companySize.employeeCount,
    }));

    // Call api to update employee count and user type information
    callApiToSaveData();
  };

  /**
   * Call update api to save all the user entered information
   */
  const callApiToSaveData = () => {
    setLoading(true);

    // Prepare correct format for request body
    let postData = prepareWorkDetails(userData, companySize);

    // Call api to update/save user information
    updateInfo(postData, config)
      .then(() => {
        // Set state for current page
        setPage("WorkSizeDetails");
        setLoading(false);
      })
      .catch((err) => {
        if (err?.response?.status === 500) {
          setError(string.SOMETHING_WENT_WRONG_TRY_AGAIN);
        } else if (err.response && err.response.data && err.response.data.errorDescription) {
          setError(err.response.data.errorDescription);
        }
        setLoading(false);
      });
  };

  /**
   * Back button handler
   *
   * @param {*} e Event for Back button click
   */
  const handleBackButtonChange = (e) => {
    e.preventDefault();
    // Set state for current page
    setPage("PersonalDetails");
  };

  /**
   * Count selection handler
   *
   * @param {*} e Event on click
   * @param {String} count Employee count
   */
  const handleSelection = (e, count) => {
    e.preventDefault();
    setError(null);
    setOrg(count);
    let idx = employeeCount.filter((item) => item.count === count);
    if (idx[0].id == 1) {
      // Set user type
      setCompanySize((prev) => ({ ...prev, userType: "OWNER_OPERATOR", employeeCount: count }));
    } else if (idx[0].id == 2 || idx[0].id == 3) {
      // Set user type
      setCompanySize((prev) => ({ ...prev, userType: "COMPANY", employeeCount: count }));
    } else {
      // Set user type
      setCompanySize((prev) => ({ ...prev, userType: "ENTERPRISE", employeeCount: count }));
    }
  };

  return (
    <div className="main">
      {/* Head text */}
      <div className="headText">{string.WORK_DETAILS}</div>
      {/* Line under head text */}
      <div className="line"></div>
      {/* Sub-heading under line */}
      <div className="text"> {string.HOW_MANY_EMPLOYEES_HELPERS_DO_YOU_HAVE}</div>
      {/* Options for count of employee */}
      <div className={Styles.container}>
        {employeeCount.map((item, i) => (
          <div
            key={i}
            className={Styles.option}
            id={companySize.employeeCount == item.count ? "greenBorder" : "greyBorder"}
            onClick={(e) => handleSelection(e, item.count)}
          >
            <img src={item.icon} alt="Employee Count" className={Styles.imgIcon}></img>
            <p className={Styles.innerText}>{item.count}</p>
          </div>
        ))}
        {error ? <div className="errorText">{error}</div> : null}
      </div>

      {/* Next button */}
      <div>
        <button className="nextButton" onClick={!loading ? handleNextButtonChange : null}>
          {" "}
          {loading ? <Loader /> : string.NEXT}
        </button>
      </div>
      {/* Back button */}
      <div>
        <button className="backButton" onClick={handleBackButtonChange}>
          {string.BACK}
        </button>
      </div>
    </div>
  );
};

export default WorkDetails;
